/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/CommandCard.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import type { ComponentType } from 'react';
import { ReceiptText, BookPlus, Undo2, ListChecks, Brain, Shield, Users, Landmark, Sparkles, Play, Pencil, CircleCheck, TriangleAlert, Info, RotateCcw, ArrowRight, Download, ScrollText, Archive } from 'lucide-react';
import { I, Badge, Spinner } from './ui';
import { Findings } from './AgentCards';
import { amt } from '../lib/format';
import { navigate } from '../lib/router';

export interface PreviewLine { account: string; name: string; side: 'D' | 'C'; amount: number; vatCode: string | null; tax: boolean }
export interface CommandData {
  kind: 'command'; commandId: string; command: string; icon: string; status: 'draft' | 'executed' | 'cancelled' | 'failed' | 'stale';
  preview: {
    title: string; headline?: string | null; sub?: string | null; verb: string; fields: [string, string][]; lines?: PreviewLine[];
    items?: { label: string; detail?: string; amount?: string; flag?: string }[]; skipped?: { label: string; reason: string }[];
    findings?: { severity: string; message: string }[]; notes?: string[];
  };
  result: { summary: string; fields?: [string, string][]; links?: { label: string; href: string; api?: boolean }[]; undo?: string | null; qr?: string | null } | null;
  error: string | null; instruction: string | null;
}

export const COMMAND_ICONS: Record<string, ComponentType<any>> = { 'receipt-text': ReceiptText, 'book-plus': BookPlus, 'undo-2': Undo2, 'list-checks': ListChecks, brain: Brain, shield: Shield, users: Users, landmark: Landmark,
  'scroll-text': ScrollText, archive: Archive };
const STATUS: Record<CommandData['status'], [string, 'accent' | 'pos' | 'warn' | 'neg' | undefined]> = {
  draft: ['Entwurf', 'accent'], executed: ['Ausgeführt', 'pos'], cancelled: ['Abgebrochen', undefined], failed: ['Fehlgeschlagen', 'neg'], stale: ['Veraltet', 'warn'],
};

/**
 * The confirmation card (Mercury Command pattern): everything that will happen, before it happens.
 * A draft executes only on the human's click (or Cmd+Enter on the latest draft); afterwards the same
 * card turns into the receipt with links and an undo that drafts the reversal.
 */
export function CommandCard({ tid, card, primary, acting, onExecute, onCancel, onAdjust, onSend }: {
  tid: string; card: CommandData; primary: boolean; acting: 'exec' | 'cancel' | null;
  onExecute: () => void; onCancel: () => void; onAdjust: () => void; onSend: (text: string) => void;
}) {
  const p = card.preview;
  const r = card.result;
  const [label, kind] = STATUS[card.status] ?? [card.status, undefined];
  const go = (href: string) => navigate(`/t/${tid}${href}`);
  return (
    <article className="ccard" data-status={card.status} data-primary={primary || undefined} aria-label={`${p.title}: ${label}`}>
      <header className="ccard-head">
        <span className="ccard-icon" aria-hidden><I icon={COMMAND_ICONS[card.icon] ?? Sparkles} sm /></span>
        <span className="title">{p.title}</span>
        <Badge kind={kind} dot>{label}</Badge>
      </header>

      {(p.headline || p.sub) && (
        <div className="ccard-hero">
          {p.headline && <div className="headline" data-copy-unit>{p.headline}</div>}
          {p.sub && <div className="sub">{p.sub}</div>}
        </div>
      )}

      <div className="ccard-body">
        {p.fields.length > 0 && (
          <dl className="ccard-fields">
            {p.fields.map(([k, v]) => <div key={k}><dt>{k}</dt><dd data-copy-unit>{v}</dd></div>)}
          </dl>
        )}
        {p.items && p.items.length > 0 && (
          <ul className="ccard-items">
            {p.items.map((it, i) => (
              <li key={i}><span className="t"><b>{it.label}</b>{it.detail && <span>{it.detail}{it.flag && <> · <Badge kind="warn">{it.flag}</Badge></>}</span>}</span>{it.amount && <span className="num">{it.amount}</span>}</li>
            ))}
          </ul>
        )}
        {p.lines && p.lines.length > 0 && (
          <table className="lines ccard-lines" data-copy-surface aria-label="Buchungszeilen">
            <tbody>{p.lines.map((l, i) => (
              <tr key={i} className={l.tax ? 'tax' : undefined}>
                <td className="muted side">{l.side === 'D' ? 'Soll' : 'Haben'}</td>
                <td><span className="mono">{l.account}</span> <span className="acct">{l.name}</span>{l.vatCode && <> <Badge>{l.vatCode}</Badge></>}</td>
                <td className="r num">{amt(l.amount)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {p.skipped && p.skipped.length > 0 && (
          <details className="ccard-skipped">
            <summary>{p.skipped.length} ausgelassen</summary>
            <ul>{p.skipped.map((s, i) => <li key={i}><b>{s.label}</b><span>{s.reason}</span></li>)}</ul>
          </details>
        )}
        {card.status === 'draft' && <Findings findings={p.findings ?? []} />}
        {card.status === 'draft' && p.notes && p.notes.length > 0 && (
          <ul className="ccard-notes">{p.notes.map((n, i) => <li key={i}><I icon={Info} sm /><span>{n}</span></li>)}</ul>
        )}
      </div>

      {card.status === 'executed' && r && (
        <div className="ccard-result" role="status">
          <p className="summary"><I icon={CircleCheck} sm /><span>{r.summary}</span></p>
          {r.qr && (
            <figure className="ccard-qr">
              <img src={`/api/t/${tid}${r.qr}`} alt="QR-Code des Zahlteils" width={128} height={128} />
              <figcaption>QR-Zahlteil<br /><span className="dim">Mit jeder Banking-App scannbar</span></figcaption>
            </figure>
          )}
        </div>
      )}
      {(card.status === 'stale' || card.status === 'failed') && card.error && (
        <div className={`ccard-error ${card.status}`} role="alert"><I icon={TriangleAlert} sm /><span>{card.error}</span></div>
      )}

      <footer className="ccard-foot">
        {card.status === 'draft' && <>
          {/* The ⌘↵ hint lives in the composer bar while this is the latest draft; the button only declares it. */}
          <button className="btn primary sm" onClick={onExecute} disabled={!!acting} data-command-execute aria-keyshortcuts={primary ? 'Meta+Enter Control+Enter' : undefined} title={primary ? `${p.verb} (⌘↵)` : undefined}>
            {acting === 'exec' ? <Spinner /> : <I icon={Play} sm />} {p.verb}
          </button>
          <button className="btn sm" onClick={onAdjust} disabled={!!acting}><I icon={Pencil} sm /> Anpassen</button>
          <button className="btn ghost sm" onClick={onCancel} disabled={!!acting}>{acting === 'cancel' && <Spinner />} Abbrechen</button>
        </>}
        {card.status === 'executed' && r && <>
          {r.links?.map((l) => l.api
            ? <a key={l.href} className="btn sm" href={`/api/t/${tid}${l.href}`} download><I icon={Download} sm /> {l.label}</a>
            : <button key={l.href} className="btn sm" onClick={() => go(l.href)}>{l.label} <I icon={ArrowRight} sm /></button>)}
          {r.undo && <button className="btn ghost sm" onClick={() => onSend(r.undo!)}><I icon={Undo2} sm /> Rückgängig</button>}
        </>}
        {(card.status === 'stale' || card.status === 'cancelled') && card.instruction && (
          <button className="btn sm" onClick={() => onSend(card.instruction!)}><I icon={RotateCcw} sm /> Neu entwerfen</button>
        )}
        {card.status === 'failed' && card.instruction && <button className="btn sm" onClick={onAdjust}><I icon={Pencil} sm /> Anpassen</button>}
      </footer>
    </article>
  );
}

/** Quick replies: the value of an option is sent as the next message. */
export function ChoiceCard({ card, onSend, disabled }: { card: { question: string; options: { label: string; value: string; detail?: string }[] }; onSend: (text: string) => void; disabled: boolean }) {
  if (!card.options?.length) return null;
  return (
    <div className="choices" role="group" aria-label={card.question}>
      {card.options.map((o) => (
        <button key={o.value} className="choice" onClick={() => onSend(o.value)} disabled={disabled}>
          <span className="t"><b>{o.label}</b>{o.detail && <span>{o.detail}</span>}</span><I icon={ArrowRight} sm />
        </button>
      ))}
    </div>
  );
}
